if (!customElements.get('filter-az-nav')) {
	class FilterAzNav extends HTMLElement {
		static #LETTER_SELECTOR = '.az-letter';
		static #DISABLED_CLASS = 'is-disabled';

		#unsubscribe = null;

		// Waiting for the definition means either script can load first. Defining <filterable-list> upgrades the list
		// around this rail before the wait resolves, so its subscribe listener is in place. The rail may have left the
		// page, or subscribed from an earlier connect, by the time the wait resolves.
		async connectedCallback() {
			if (customElements.get('filterable-list') == null) {
				this.#watchForMissingList();
			}
			const FilterableList = await customElements.whenDefined('filterable-list');
			if (!this.isConnected || this.#unsubscribe != null) {
				return;
			}
			this.#subscribe(FilterableList.VISIBLE_ITEMS_SUBSCRIBE_EVENT);
		}

		disconnectedCallback() {
			this.#unsubscribe?.();
			this.#unsubscribe = null;
		}

		// The wait never settles when filterable-list.js is missing, so a list still undefined once the page has loaded
		// is reported rather than leaving the rail silently unlinked.
		#watchForMissingList() {
			if (document.readyState === 'complete') {
				this.#warnIfListUndefined();
				return;
			}
			window.addEventListener('load', () => this.#warnIfListUndefined(), { once: true });
		}

		#warnIfListUndefined() {
			if (customElements.get('filterable-list') == null) {
				console.warn('[filter-az-nav] filterable-list.js is not loaded; the rail stays unlinked.');
			}
		}

		#subscribe(eventName) {
			this.dispatchEvent(this.#buildVisibleItemsSubscribeEvent(eventName));
			// dispatchEvent is synchronous, so an unanswered request means no ancestor provider heard it.
			if (this.#unsubscribe == null) {
				console.warn('[filter-az-nav] Subscribe request unanswered: the rail must live inside a <filterable-list>.');
			}
		}

		#buildVisibleItemsSubscribeEvent(eventName) {
			return new CustomEvent(eventName, {
				bubbles: true,
				detail: {
					callback: (visibleItems, unsubscribe) => this.#receiveVisibleItems(visibleItems, unsubscribe),
				},
			});
		}

		#receiveVisibleItems(visibleItems, unsubscribe) {
			if (unsubscribe != null) {
				this.#unsubscribe = unsubscribe;
			}
			this.#sync(visibleItems);
		}

		#sync(visibleItems) {
			const firstIdByKey = this.#firstIdByKey(visibleItems);
			this.#letters().forEach((letter) => this.#retargetLetter(letter, firstIdByKey));
		}

		#firstIdByKey(visibleItems) {
			const map = new Map();
			visibleItems.forEach((item) => {
				if (item.key !== '' && !map.has(item.key)) {
					map.set(item.key, item.id);
				}
			});
			return map;
		}

		#letters() {
			return this.querySelectorAll(FilterAzNav.#LETTER_SELECTOR);
		}

		#retargetLetter(el, firstIdByKey) {
			const targetId = firstIdByKey.get(el.dataset.filterKey);
			if (targetId != null) {
				this.#enableLetter(el, targetId);
			} else {
				this.#disableLetter(el);
			}
		}

		#enableLetter(el, targetId) {
			el.classList.remove(FilterAzNav.#DISABLED_CLASS);
			el.removeAttribute('aria-disabled');
			el.setAttribute('href', `#${targetId}`);
		}

		#disableLetter(el) {
			el.classList.add(FilterAzNav.#DISABLED_CLASS);
			el.setAttribute('aria-disabled', 'true');
			el.removeAttribute('href');
		}
	}

	customElements.define('filter-az-nav', FilterAzNav);
}
